import { inject, Service } from '@angular/core';
import { Observable, combineLatest as observableCombineLatest, of } from 'rxjs';
import { first, map, shareReplay, tap } from 'rxjs/operators';
import { ApiClient } from '@app/api/api-client';
import { ApiTypes } from '@app/api/api-types';
import { Api } from '@app/api/generated/api';
import { AccountService } from '@core/providers/account/account.service';
import { CONSTANTS } from '@shared/constants';

@Service()
export class CategoriesBackendService {
  private api = inject(ApiClient);
  private accountService = inject(AccountService);

  private cachedCategories = new Map<string, ApiTypes.SurveyCategoryListItemResponseDto>();
  private contextUuid?: string;
  private categoriesCachedRequest?: Observable<ApiTypes.SurveyCategoryListItemResponseDto[]>;

  getAllCategories(
    contextUuid: string,
    contextType: string,
    selectedGroupsUuids?: string[]
  ): Observable<ApiTypes.SurveyCategoryListItemResponseDto[]> {
    if (this.contextUuid === contextUuid && this.cachedCategories.size) {
      return of(Array.from(this.cachedCategories.values()));
    }

    this.clearCachedData();

    if (this.contextUuid === contextUuid && this.categoriesCachedRequest) {
      return this.categoriesCachedRequest;
    }

    this.contextUuid = contextUuid;
    const request$ =
      contextType === CONSTANTS.CONTEXT.ACCOUNT
        ? this.api.get(Api.surveys.categories.accounts, {
            pathParams: { accountUuid: contextUuid },
          })
        : this.api.get(Api.surveys.categories.venues, {
            pathParams: { venueUuid: contextUuid },
          });

    this.categoriesCachedRequest = request$.pipe(
      tap(categories => {
        categories.forEach(category => this.cachedCategories.set(category.uuid, category));
      }),
      shareReplay(1)
    );

    return this.categoriesCachedRequest;
  }

  getAllCategoriesForLoggedAccount(
    contextUuid: string,
    contextType: string,
    selectedGroupsUuids?: string[]
  ): Observable<ApiTypes.SurveyCategoryListItemResponseDto[]> {
    return observableCombineLatest([
      this.accountService.getAccountContext$().pipe(first()),
      this.getAllCategories(contextUuid, contextType, selectedGroupsUuids),
    ]).pipe(
      map(([account, categories]) =>
        categories.filter(category => category.categoryType === account.masterData?.questionSetUuid)
      )
    );
  }

  getCategoryDetails(
    categoryUuid: string,
    contextUuid: string,
    contextType: string
  ): Observable<ApiTypes.SurveyCategoryResponseDto> {
    const cached = this.cachedCategories.get(categoryUuid);
    if (cached) {
      return of(cached);
    }

    return contextType === CONSTANTS.CONTEXT.ACCOUNT
      ? this.api.get(Api.surveys.categories.accounts.byAccountUuid, {
          pathParams: { uuid: categoryUuid, accountUuid: contextUuid },
        })
      : this.api.get(Api.surveys.categories.venues.byVenueUuid, {
          pathParams: { uuid: categoryUuid, venueUuid: contextUuid },
        });
  }

  getOverallExperienceDetails(
    contextUuid: string,
    contextType: string,
    selectedGroupsUuids?: string[]
  ): Observable<ApiTypes.SurveyCategoryListItemResponseDto | undefined> {
    return this.getAllCategoriesForLoggedAccount(
      contextUuid,
      contextType,
      selectedGroupsUuids
    ).pipe(map(categories => categories.find(category => category.overallExperience)));
  }

  clearCachedData() {
    this.cachedCategories = new Map<string, ApiTypes.SurveyCategoryListItemResponseDto>();
    this.categoriesCachedRequest = undefined;
  }
}
